import * as React from 'react';
import { makeStyles, createStyles, Theme, Dialog, DialogContent, DialogTitle, DialogProps, Box, IconButton } from '@material-ui/core';
import { Check as CheckIcon, Autorenew as LoadingIcon, Close as CloseIcon, Warning as WarningIcon, AttachMoney as MoneyIcon, Info as InfoIcon } from '@material-ui/icons';
import cn from 'classnames';

type IconDialogProps = DialogProps & {
  icon: 'check' | 'loading' | 'warning' | 'money' | React.ReactNode;
  rotating?: boolean;
  closeBtn?: boolean;
  iconColor?: 'primary' | 'blue';
  showIcon?: boolean;
};

const getIcon = (icon: IconDialogProps['icon']) => {
  switch (icon) {
    default:
    case 'check': return CheckIcon;
    case 'loading': return LoadingIcon;
    case 'warning': return WarningIcon;
    case 'money': return MoneyIcon;
    case 'info': return InfoIcon;
  }
};

const IconDialog: React.FC<IconDialogProps> = (props) => {
  const { children, icon, rotating, closeBtn, iconColor, showIcon = true, ...rest } = props;
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();
  const simpleIcon = typeof icon === 'string';

  const Icon = getIcon(icon);
  const handleClose = (ev: any) => rest.onClose && rest.onClose(ev, 'backdropClick');

  return (
    <Dialog
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
      classes={{ paper: cn(classes.paper, rest.classes?.paper) }}
      data-test={`IconDialog-${icon}-${rest.open ? 'open' : 'closed'}`}
    >
      <DialogTitle className={cn(classes.dialogTitle, classes.iconBox)}>
        {showIcon && (
        <Box className={cn(
          classes.iconWrapper,
          simpleIcon && classes.iconWrapperFilled,
          simpleIcon && classes[(`iconWrapperFilled_${iconColor || 'primary'}` as 'icon')],
        )}
        >
          {!simpleIcon
            ? icon
            : <Icon className={cn(classes.icon, rotating && classes.iconRotating)} />}
        </Box>
        )}
        {closeBtn && rest.onClose && (
        <IconButton onClick={handleClose} className={classes.closeBtn}>
          <CloseIcon />
        </IconButton>
        )}
      </DialogTitle>
      <DialogContent className={classes.contentRoot}>
        {children}
      </DialogContent>
    </Dialog>
  );
};

const useStyles = makeStyles((theme: Theme) => {
  const iconSize = 45;
  const padding = 2;
  const buttonPadding = 1.5;
  return createStyles({
    paper: {
      padding: theme.spacing(padding),
      width: '100%',
      overflow: 'visible',
      maxHeight: 'calc(100% - 64px - 32px)',
      marginTop: 64,
    },
    dialogTitle: {
      padding: 0,
      flex: '',
      margin: 0,
    },
    iconBox: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      height: iconSize / 2 + theme.spacing(buttonPadding),
    },
    iconWrapper: {
      borderRadius: '50%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      transform: 'translate(0, -50%)',
      marginTop: theme.spacing(-padding),
      boxShadow: theme.shadows[4],
    },
    iconWrapperFilled: {
      color: '#ffffff',
      padding: theme.spacing(buttonPadding),
    },
    iconWrapperFilled_primary: { backgroundColor: theme.palette.primary.main },
    iconWrapperFilled_blue: { backgroundColor: '#5505BC' },
    icon: {
      width: iconSize,
      height: iconSize,
    },
    iconRotating: {
      animationName: '$IconDialogIconRotating',
      animationDuration: '1s',
      animationTimingFunction: 'linear',
      animationIterationCount: 'infinite',
    },
    '@keyframes IconDialogIconRotating': {
      from: { transform: 'rotate(0deg)' },
      to: { transform: 'rotate(360deg)' },
    },
    closeBtn: {
      position: 'absolute',
      top: 2,
      right: 2,
      padding: theme.spacing(1),
    },
    contentRoot: {
      padding: theme.spacing(1),
    },
  });
});

export default IconDialog;
